import { useState } from 'react'
import { theme } from '../theme'
import { t } from '../lib/i18n'

/**
 * Two small charts for the parent's Progress tab (8 Oct 2026).
 *
 * Both are one series, so one colour (the app's green) and no legend: the
 * title above says what it is. Thin bars with rounded tops, a quiet axis,
 * a label only on the bar that matters (the biggest), and the exact number
 * on hover or tap. A "Show as a table" switch sits under each, for anyone
 * who would rather read numbers than shapes.
 */

export function BarChart({
  bars, unit, height = 150,
}: { bars: { label: string; value: number }[]; unit: string; height?: number }) {
  const [hover, setHover] = useState<number | null>(null)
  const [table, setTable] = useState(false)
  const max = Math.max(1, ...bars.map((b) => b.value))
  const peak = bars.reduce((best, b, i) => (b.value > (bars[best]?.value ?? -1) ? i : best), 0)
  const W = 100 / Math.max(1, bars.length)

  if (table) return <Table rows={bars.map((b) => [b.label, `${b.value} ${unit}`])} onChart={() => setTable(false)} />

  return (
    <div>
      <div role="img" aria-label={bars.map((b) => `${b.label}: ${b.value} ${unit}`).join(', ')}
        style={{ position: 'relative', height, display: 'flex', alignItems: 'flex-end', gap: 2,
                 borderBottom: `1px solid ${theme.border}`, padding: '18px 0 0' }}>
        {bars.map((b, i) => {
          const h = b.value ? Math.max(4, (b.value / max) * (height - 30)) : 2
          const on = hover === i
          return (
            <div key={b.label + i} style={{ width: `${W}%`, height: '100%', display: 'flex', flexDirection: 'column',
                                            justifyContent: 'flex-end', alignItems: 'center', cursor: 'default' }}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
              onClick={() => setHover(on ? null : i)}>
              {(on || (i === peak && b.value > 0 && hover === null)) ? (
                <div style={{ fontSize: 12, fontWeight: 800, color: theme.text2, marginBottom: 3, whiteSpace: 'nowrap' }}>
                  {b.value}{on ? ` ${unit}` : ''}
                </div>
              ) : null}
              <div style={{
                width: 'min(70%, 34px)', height: h, borderRadius: '4px 4px 0 0',
                background: b.value ? (on ? theme.greenDark : theme.green) : theme.borderSoft,
                transition: 'background 0.15s',
              }} />
            </div>
          )
        })}
      </div>
      <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
        {bars.map((b, i) => (
          <div key={b.label + i} style={{ width: `${W}%`, textAlign: 'center', fontSize: 12, color: theme.muted }}>
            {b.label}
          </div>
        ))}
      </div>
      <TableToggle onTable={() => setTable(true)} />
    </div>
  )
}

export function TrendChart({
  points, unit, height = 130,
}: { points: { label: string; value: number }[]; unit: string; height?: number }) {
  const [hover, setHover] = useState<number | null>(null)
  const [table, setTable] = useState(false)
  if (table) return <Table rows={points.map((p) => [p.label, `${p.value} ${unit}`])} onChart={() => setTable(false)} />

  const vw = 300
  const vh = 100
  const lo = Math.min(...points.map((p) => p.value))
  const hi = Math.max(lo + 1, ...points.map((p) => p.value))
  const x = (i: number) => (points.length < 2 ? vw / 2 : (i / (points.length - 1)) * (vw - 16) + 8)
  const y = (v: number) => vh - 8 - ((v - lo) / (hi - lo)) * (vh - 26)
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i)} ${y(p.value)}`).join(' ')
  const area = `${line} L${x(points.length - 1)} ${vh} L${x(0)} ${vh} Z`
  const last = points[points.length - 1]
  const shown = hover ?? points.length - 1
  const sp = points[shown]

  return (
    <div>
      <svg viewBox={`0 0 ${vw} ${vh}`} width="100%" height={height} preserveAspectRatio="none"
        role="img" aria-label={points.map((p) => `${p.label}: ${p.value}`).join(', ')}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect()
          const at = ((e.clientX - r.left) / r.width) * vw
          let best = 0
          points.forEach((_, i) => { if (Math.abs(x(i) - at) < Math.abs(x(best) - at)) best = i })
          setHover(best)
        }}
        style={{ display: 'block', overflow: 'visible' }}>
        <path d={area} fill={theme.greenPale} />
        <path d={line} fill="none" stroke={theme.green} strokeWidth={2} vectorEffect="non-scaling-stroke"
          strokeLinejoin="round" strokeLinecap="round" />
        {hover !== null ? (
          <line x1={x(hover)} x2={x(hover)} y1={0} y2={vh} stroke={theme.border} strokeWidth={1}
            vectorEffect="non-scaling-stroke" />
        ) : null}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: theme.muted, marginTop: 4 }}>
        <span>{points[0]?.label}</span>
        <span style={{ fontWeight: 800, color: theme.text2 }}>
          {sp ? `${sp.label}: ${sp.value} ${unit}` : last ? `${last.value} ${unit}` : ''}
        </span>
        <span>{last?.label}</span>
      </div>
      <TableToggle onTable={() => setTable(true)} />
    </div>
  )
}

function TableToggle({ onTable }: { onTable: () => void }) {
  return (
    <button type="button" onClick={onTable} style={{
      marginTop: 6, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
      fontSize: 13, fontWeight: 700, color: theme.blue, fontFamily: 'inherit',
    }}>{t('Show as a table')}</button>
  )
}

function Table({ rows, onChart }: { rows: string[][]; onChart: () => void }) {
  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${theme.borderSoft}` }}>
              <td style={{ padding: '5px 4px', color: theme.text2 }}>{r[0]}</td>
              <td style={{ padding: '5px 4px', color: theme.text, fontWeight: 700, textAlign: 'right' }}>{r[1]}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" onClick={onChart} style={{
        marginTop: 6, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
        fontSize: 13, fontWeight: 700, color: theme.blue, fontFamily: 'inherit',
      }}>{t('Show as a chart')}</button>
    </div>
  )
}
